// V6: WorkflowDestinationPicker — the Notion-style flat list for
// choosing where a saved contract goes (which workflow, which category,
// or create-new at any level).
//
// Two surfaces use this component:
//   1. Pre-emptive: chat composer toolbar button opens the picker.
//      Selection is held in useWorkflowDestination and silently
//      consumed when the user later sends.
//   2. Post-success: assistant confirmation card auto-opens the picker
//      if no pre-set was chosen.
//
// Design constraints baked in:
//   - All categories visible at once (no dropdowns/menus)
//   - + buttons inline at every level (workflow, category)
//   - Empty default categories still render (so first-time users see
//     familiar groupings)
//   - Search filter at the top to handle long lists
//   - Single Save action at the bottom; bottom bar shows the selected
//     destination as a breadcrumb so the user always knows what they're
//     about to save
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { APP } from '../tokens';
import { useWorkflowCategories, type PickerCategory } from '../../../hooks/useWorkflowCategories';
import type { WorkflowDestination } from '../../../hooks/useWorkflowDestination';
import type { UserWorkflowDTO } from '../../../api/workflowApi';
import { EmojiPicker } from './EmojiPicker';
import { AppFolderIcon } from '../AppFolderIcon';

// Emoji per preset ID — mirrors WorkflowsPanel.WORKFLOW_EMOJI
const WORKFLOW_EMOJI: Record<string, string> = {
  contract_send: '📨',
  contract_review: '📑',
  lease_agreement_review: '🏠',
  employee_agreement_review: '👤',
  extract_obligations_and_deadlines: '📋',
  draft_client_summary: '✏️',
  draft_client_alert: '🚨',
  compliance_notice_summary: '🛡️',
  generate_post_closing_timeline: '⏰',
};

function workflowEmoji(wf: UserWorkflowDTO): string {
  if (wf.icon) return wf.icon;
  if (wf.parentPresetId && WORKFLOW_EMOJI[wf.parentPresetId]) return WORKFLOW_EMOJI[wf.parentPresetId];
  return '⚡';
}

export interface WorkflowDestinationPickerProps {
  open: boolean;
  /** Suggested template name (usually the doc filename without extension). */
  defaultTemplateName?: string;
  /** Called when the user picks (or creates) a destination + clicks Save. */
  onSelect: (destination: WorkflowDestination & object) => void | Promise<void>;
  /** Called when the user cancels or clicks the backdrop. */
  onClose: () => void;
}

interface SelectedTarget {
  kind: 'existing' | 'newWorkflow';
  workflow?: UserWorkflowDTO;
  category?: PickerCategory;
  newWorkflowName?: string;
}

export function WorkflowDestinationPicker({
  open,
  defaultTemplateName,
  onSelect,
  onClose,
}: WorkflowDestinationPickerProps) {
  const { categories, loading, refresh } = useWorkflowCategories(open);

  // Picker state.
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<SelectedTarget | null>(null);
  const [templateName, setTemplateName] = useState(defaultTemplateName ?? '');
  const [newCategoryDraft, setNewCategoryDraft] = useState<string | null>(null);
  // Per-category inline "new workflow" input state, keyed by category name.
  const [newWorkflowDrafts, setNewWorkflowDrafts] = useState<Record<string, string>>({});
  // Local-only categories the user has created via "+ New category"
  // but hasn't saved a workflow under yet. Merged into the rendered
  // list so the inline new-workflow input appears under them. Cleared
  // on picker close.
  const [draftCategories, setDraftCategories] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [newWorkflowIcon, setNewWorkflowIcon] = useState<string | null>(null);

  // Reset transient state on open. defaultTemplateName might change between
  // opens (different doc), so re-sync the input.
  useEffect(() => {
    if (open) {
      setSearch('');
      setSelected(null);
      setTemplateName(defaultTemplateName ?? '');
      setNewCategoryDraft(null);
      setNewWorkflowDrafts({});
      setDraftCategories([]);
      setNewWorkflowIcon(null);
    }
  }, [open, defaultTemplateName]);

  // Refresh on open so a workflow created elsewhere shows up immediately.
  useEffect(() => { if (open) void refresh(); }, [open, refresh]);

  // Merge server categories with the user's local-only drafts so newly
  // typed categories appear in the list immediately. Server categories
  // win on name collisions (no duplicate sections).
  const allCategories = useMemo(() => {
    const serverNames = new Set(categories.map((c) => c.name));
    const drafts: PickerCategory[] = draftCategories
      .filter((name) => !serverNames.has(name))
      .map((name) => ({ name, workflowCount: 0, workflows: [], isDefault: false }));
    return [...categories, ...drafts];
  }, [categories, draftCategories]);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allCategories;
    return allCategories
      .map((cat) => ({
        ...cat,
        workflows: cat.workflows.filter((wf) => wf.name.toLowerCase().includes(q)),
      }))
      .filter((cat) => cat.name.toLowerCase().includes(q) || cat.workflows.length > 0);
  }, [allCategories, search]);

  if (!open) return null;

  const canSubmit = !!selected && templateName.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit || !selected) return;
    setSubmitting(true);
    try {
      const cleanedTemplateName = templateName.trim();
      if (selected.kind === 'existing' && selected.workflow) {
        await onSelect({
          mode: 'existing',
          workflowId: selected.workflow.id,
          workflowName: selected.workflow.name,
          categoryName: selected.workflow.userCategory ?? 'General',
          templateName: cleanedTemplateName,
        });
      } else if (selected.kind === 'newWorkflow' && selected.category && selected.newWorkflowName) {
        await onSelect({
          mode: 'newWorkflow',
          newCategory: selected.category.name,
          newWorkflowName: selected.newWorkflowName,
          templateName: cleanedTemplateName,
          ...(newWorkflowIcon ? { icon: newWorkflowIcon } : {}),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumb = (() => {
    if (!selected) return 'Select a workflow or create a new one';
    if (selected.kind === 'existing' && selected.workflow) {
      const cat = selected.workflow.userCategory ?? 'General';
      return `${cat} → ${selected.workflow.name}`;
    }
    if (selected.kind === 'newWorkflow' && selected.category && selected.newWorkflowName) {
      return `${selected.category.name} → ${selected.newWorkflowName} (new)`;
    }
    return '';
  })();

  return (
    <div onClick={onClose} style={BACKDROP}>
      <div onClick={(e) => e.stopPropagation()} style={CARD}>
        <header style={HEADER}>
          <span style={ICON}>💾</span>
          <span style={TITLE}>Save this to a workflow</span>
        </header>

        <input
          type="text"
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workflows…"
          style={SEARCH_INPUT}
        />

        <div style={BODY}>
          {loading && categories.length === 0 && <div style={EMPTY}>Loading…</div>}

          {filteredCategories.map((cat) => (
            <CategorySection
              key={cat.name}
              category={cat}
              search={search}
              selected={selected}
              onSelectWorkflow={(wf) => setSelected({ kind: 'existing', workflow: wf })}
              newWorkflowDraft={newWorkflowDrafts[cat.name] ?? null}
              newWorkflowIcon={newWorkflowDrafts[cat.name] != null ? newWorkflowIcon : null}
              onChangeNewWorkflowIcon={setNewWorkflowIcon}
              onStartNewWorkflow={() => { setNewWorkflowDrafts((prev) => ({ ...prev, [cat.name]: '' })); setNewWorkflowIcon(null); }}
              onChangeNewWorkflowName={(name) => {
                setNewWorkflowDrafts((prev) => ({ ...prev, [cat.name]: name }));
                if (name.trim()) {
                  setSelected({ kind: 'newWorkflow', category: cat, newWorkflowName: name.trim() });
                }
              }}
              onCancelNewWorkflow={() => {
                setNewWorkflowDrafts((prev) => {
                  const next = { ...prev };
                  delete next[cat.name];
                  return next;
                });
                setNewWorkflowIcon(null);
                if (selected?.kind === 'newWorkflow' && selected.category?.name === cat.name) {
                  setSelected(null);
                }
              }}
              onSubmit={() => void handleSubmit()}
            />
          ))}

          {/* + New category */}
          {newCategoryDraft === null ? (
            <button type="button" style={ADD_CATEGORY_BTN} onClick={() => setNewCategoryDraft('')}>
              + New category
            </button>
          ) : (
            <div style={NEW_CATEGORY_ROW}>
              <input
                autoFocus
                type="text"
                value={newCategoryDraft}
                onChange={(e) => setNewCategoryDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') setNewCategoryDraft(null);
                  if (e.key === 'Enter' && newCategoryDraft.trim()) {
                    // "Create on Save" semantics — adding to draftCategories
                    // makes it render as a new section with its own
                    // "+ New workflow" button. Auto-opens the inline
                    // workflow input so the user keeps typing without an
                    // extra click. The category only persists server-side
                    // when the user saves a workflow under it.
                    const name = newCategoryDraft.trim();
                    setDraftCategories((prev) => prev.includes(name) ? prev : [...prev, name]);
                    setNewWorkflowDrafts((prev) => ({ ...prev, [name]: '' }));
                    setNewCategoryDraft(null);
                  }
                }}
                placeholder="Category name (Enter to confirm)"
                style={NEW_CATEGORY_INPUT}
              />
              <button type="button" style={MUTED_BTN} onClick={() => setNewCategoryDraft(null)}>
                Cancel
              </button>
            </div>
          )}
        </div>

        <footer style={FOOTER}>
          <div style={BREADCRUMB_ROW}>
            <span style={BREADCRUMB_LABEL}>→</span>
            <span style={BREADCRUMB_TEXT}>{breadcrumb}</span>
          </div>

          {selected && (
            <div style={NAME_INPUT_ROW}>
              <label style={NAME_LABEL}>Template name</label>
              <input
                type="text"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && canSubmit) {
                    e.preventDefault();
                    void handleSubmit();
                  }
                }}
                placeholder="e.g. Acme MSA — Jan 2026"
                style={NAME_INPUT}
              />
            </div>
          )}

          <div style={ACTIONS}>
            <button type="button" style={SECONDARY_BTN} onClick={onClose} disabled={submitting}>
              Cancel
            </button>
            <button
              type="button"
              style={{ ...PRIMARY_BTN, opacity: canSubmit ? 1 : 0.5, cursor: canSubmit ? 'pointer' : 'not-allowed' }}
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ─── Subcomponent: one category's section ───────────────────────────

function CategorySection({
  category,
  search,
  selected,
  onSelectWorkflow,
  newWorkflowDraft,
  newWorkflowIcon,
  onChangeNewWorkflowIcon,
  onStartNewWorkflow,
  onChangeNewWorkflowName,
  onCancelNewWorkflow,
  onSubmit,
}: {
  category: PickerCategory;
  search: string;
  selected: SelectedTarget | null;
  onSelectWorkflow: (wf: UserWorkflowDTO) => void;
  newWorkflowDraft: string | null;
  newWorkflowIcon: string | null;
  onChangeNewWorkflowIcon: (icon: string | null) => void;
  onStartNewWorkflow: () => void;
  onChangeNewWorkflowName: (name: string) => void;
  onCancelNewWorkflow: () => void;
  /** Called on Enter in the inline new-workflow input. The picker
   *  decides whether canSubmit is true (template name present etc.).
   *  No-op when submission isn't possible. */
  onSubmit: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);

  // When the inline new-workflow draft opens, focus its input.
  useEffect(() => {
    if (newWorkflowDraft !== null) inputRef.current?.focus();
  }, [newWorkflowDraft]);

  // Hide the section entirely when filtered + nothing matches AND the
  // category itself doesn't match the query — keeps search results tight.
  const matchesCategoryName = !search || category.name.toLowerCase().includes(search.toLowerCase());
  if (search && !matchesCategoryName && category.workflows.length === 0) return null;

  return (
    <section style={CAT_SECTION}>
      <header style={CAT_HEADER}>
        <AppFolderIcon width={18} height={14} />
        <span style={CAT_NAME}>{category.name}</span>
        {category.isDefault && category.workflows.length === 0 && (
          <span style={CAT_HINT}>empty — create your first workflow here</span>
        )}
      </header>

      {category.workflows.map((wf) => {
        const isSelected = selected?.kind === 'existing' && selected.workflow?.id === wf.id;
        return (
          <button
            key={wf.id}
            type="button"
            onClick={() => onSelectWorkflow(wf)}
            style={{
              ...WORKFLOW_ROW,
              backgroundColor: isSelected ? `${APP.accentWorkflowPrimary}11` : 'transparent',
              borderColor: isSelected ? APP.accentWorkflowPrimary : 'transparent',
            }}
          >
            <span style={WF_ICON}>{workflowEmoji(wf)}</span>
            <span style={WF_NAME}>{wf.name}</span>
          </button>
        );
      })}

      {newWorkflowDraft === null ? (
        <button type="button" style={ADD_WORKFLOW_BTN} onClick={onStartNewWorkflow}>
          + New workflow in {category.name}
        </button>
      ) : (
        <div style={NEW_WORKFLOW_ROW}>
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setEmojiPickerOpen((v) => !v)}
              style={EMOJI_TRIGGER}
              title="Pick an icon"
            >
              {newWorkflowIcon ?? '+'}
            </button>
            {emojiPickerOpen && (
              <EmojiPicker
                onSelect={(emoji) => { onChangeNewWorkflowIcon(emoji); setEmojiPickerOpen(false); }}
                onClose={() => setEmojiPickerOpen(false)}
              />
            )}
          </div>
          <input
            ref={inputRef}
            type="text"
            value={newWorkflowDraft}
            onChange={(e) => onChangeNewWorkflowName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onCancelNewWorkflow();
              if (e.key === 'Enter' && newWorkflowDraft.trim()) {
                e.preventDefault();
                onSubmit();
              }
            }}
            placeholder="Workflow name (Enter to save)…"
            style={NEW_WORKFLOW_INPUT}
          />
          <button type="button" style={MUTED_BTN} onClick={onCancelNewWorkflow}>
            Cancel
          </button>
        </div>
      )}
    </section>
  );
}

// ─── Styles ─────────────────────────────────────────────────────────

const BACKDROP: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 200,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  backgroundColor: 'rgba(26, 23, 20, 0.45)',
};

const CARD: CSSProperties = {
  width: 560,
  maxWidth: 'calc(100vw - 32px)',
  maxHeight: 'calc(100vh - 80px)',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--app-bg-card-body)',
  borderRadius: 14,
  border: `1px solid ${APP.border}`,
  boxShadow: '0 24px 64px rgba(0, 0, 0, 0.25)',
  fontFamily: APP.fontFamily,
  overflow: 'hidden',
};

const HEADER: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '14px 18px 10px',
  borderBottom: `1px solid ${APP.border}`,
};

const ICON: CSSProperties = { fontSize: 18 };
const TITLE: CSSProperties = { fontSize: 14, fontWeight: 600, color: APP.textPrimary };

const SEARCH_INPUT: CSSProperties = {
  margin: '12px 18px 0',
  padding: '8px 12px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  fontSize: 13,
  fontFamily: APP.fontFamily,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  outline: 'none',
};

const BODY: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '12px 18px',
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
};

const EMPTY: CSSProperties = {
  fontSize: 12,
  color: APP.textMuted,
  padding: '8px 0',
};

const CAT_SECTION: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  paddingBottom: 6,
};

const CAT_HEADER: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  padding: '4px 4px 4px 0',
};

const CAT_NAME: CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};
const CAT_HINT: CSSProperties = {
  fontSize: 11,
  color: APP.textMuted,
  fontStyle: 'italic',
  marginLeft: 6,
};

const WORKFLOW_ROW: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8,
  padding: '8px 10px',
  border: '1px solid transparent',
  borderRadius: 8,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: APP.fontFamily,
};
const WF_ICON: CSSProperties = { fontSize: 13 };
const WF_NAME: CSSProperties = { fontSize: 13, fontWeight: 500, color: APP.textPrimary };

const ADD_WORKFLOW_BTN: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '6px 10px',
  border: 'none',
  background: 'transparent',
  color: APP.accentWorkflowPrimary,
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};

const NEW_WORKFLOW_ROW: CSSProperties = {
  display: 'flex', gap: 6,
  padding: '4px 0',
};

const NEW_WORKFLOW_INPUT: CSSProperties = {
  flex: 1,
  padding: '6px 10px',
  borderRadius: 6,
  border: `1px solid ${APP.accentWorkflowPrimary}`,
  fontSize: 13,
  fontFamily: APP.fontFamily,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  outline: 'none',
};

const ADD_CATEGORY_BTN: CSSProperties = {
  alignSelf: 'flex-start',
  padding: '8px 10px',
  marginTop: 6,
  border: `1px dashed ${APP.accentWorkflowPrimary}`,
  borderRadius: 8,
  background: 'transparent',
  color: APP.accentWorkflowPrimary,
  fontSize: 12,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};

const NEW_CATEGORY_ROW: CSSProperties = {
  display: 'flex', gap: 6,
  marginTop: 6,
};

const NEW_CATEGORY_INPUT: CSSProperties = {
  flex: 1,
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${APP.accentWorkflowPrimary}`,
  fontSize: 13,
  fontFamily: APP.fontFamily,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  outline: 'none',
};

const FOOTER: CSSProperties = {
  borderTop: `1px solid ${APP.border}`,
  padding: '12px 18px 14px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const BREADCRUMB_ROW: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 6,
  fontSize: 12,
  color: APP.textMuted,
};

const BREADCRUMB_LABEL: CSSProperties = { color: APP.accentWorkflowPrimary, fontWeight: 600 };
const BREADCRUMB_TEXT: CSSProperties = { color: APP.textPrimary };

const NAME_INPUT_ROW: CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 4,
};

const NAME_LABEL: CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: APP.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
};

const NAME_INPUT: CSSProperties = {
  padding: '8px 10px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  fontSize: 13,
  fontFamily: APP.fontFamily,
  backgroundColor: 'var(--app-bg-input)',
  color: APP.textPrimary,
  outline: 'none',
};

const ACTIONS: CSSProperties = {
  display: 'flex', gap: 8, justifyContent: 'flex-end',
  marginTop: 4,
};

const PRIMARY_BTN: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: 'none',
  backgroundColor: APP.accentWorkflowPrimary,
  color: '#FFFFFF',
  fontSize: 13,
  fontWeight: 600,
  fontFamily: APP.fontFamily,
};

const SECONDARY_BTN: CSSProperties = {
  padding: '8px 14px',
  borderRadius: 8,
  border: `1px solid ${APP.border}`,
  background: 'transparent',
  color: APP.textPrimary,
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};

const EMOJI_TRIGGER: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: 6,
  border: `1px dashed ${APP.textMuted}55`,
  background: 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  color: APP.textMuted,
  cursor: 'pointer',
  padding: 0,
  fontFamily: APP.fontFamily,
};

const MUTED_BTN: CSSProperties = {
  padding: '4px 8px',
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: APP.textMuted,
  fontSize: 12,
  cursor: 'pointer',
  fontFamily: APP.fontFamily,
};
